import {
	BehaviorSubject,
	catchError,
	firstValueFrom,
	forkJoin,
	from,
	of
} from 'rxjs';
import {Injectable} from '@angular/core';
import {environment} from '@env/environment';
import {HttpClient} from '@angular/common/http';
import {UtilsService} from "../utils.service";
import {configRegistry} from "../../../config/config-registry";


/**
 * Service to manage application configuration.
 * Handles loading configuration from S3 or local files based on the institution's rollout status.
 */
@Injectable({
	providedIn: 'root'
})
export class AppConfigService {
	// Static configuration object accessible globally
	static config: any = {};

	// Observable to notify subscribers when the app configuration is updated
	public onAppConfigUpdated$: BehaviorSubject<any> = new BehaviorSubject(null);

	// Institution ID from the environment configuration
	private institutionId = environment.institutionCode;
	configKey = this.institutionId.toLowerCase() as keyof typeof configRegistry;

	instPath = configRegistry[this.configKey];
	defaultPath = configRegistry['default'];

	// URLs for fetching configuration files from S3
	private s3URL = `${environment.s3Url}cs-config/institution-config/${this.institutionId.toUpperCase()}_config.json`;
	private s3CommonURL = `${environment.s3Url}cs-config/institution-config/common_config.json`;

	constructor(private http: HttpClient, private utilsService: UtilsService) {
	}

	/**
	 * Retrieves the static configuration object.
	 * @returns The current application configuration.
	 */
	static getConfig() {
		return AppConfigService.config;
	}

	/**
	 * Initializes the application configuration.
	 * Loads configuration from S3 or local files based on the institution's rollout status.
	 * @returns A promise that resolves when the configuration is successfully loaded.
	 */
	init(): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			//if (!appConfig.nonRolloutInstitutions.includes(this.institutionId)) {
			// Load configuration from S3 for rollout institutions
			//  this.loadFromS3().then(resolve).catch(reject);
			//} else {
			// Load local configuration for non-rollout institutions
			this.loadLocalConfig().then(resolve).catch(reject);
			//
			//}
		});
	}

	/**
	 * Fetches data from a given URL.
	 * @param url The URL to fetch data from.
	 * @returns A promise that resolves with the fetched data.
	 */
	async fetchData(url: string): Promise<any> {
		const response = await fetch(url);
		return await response.json();
	}

	/**
	 * Loads configuration from S3.
	 * Merges common and institution-specific configurations and updates the static config object.
	 * @returns A promise that resolves when the configuration is successfully loaded.
	 */
	private loadFromS3(): Promise<void> {
		return forkJoin({
			common: from(this.fetchData(this.s3CommonURL)), // Fetch common configuration
			institution: from(this.fetchData(this.s3URL)) // Fetch institution-specific configuration
		}).pipe(
			catchError(error => {
				console.error('Institution config failed, loading fallback common config only:', error);
				// Fallback to common configuration if institution-specific config fails
				return from(this.fetchData(this.s3CommonURL)).pipe(
					catchError(fallbackError => {
						console.error('Fallback common config also failed:', fallbackError);
						return of({common: null, institution: null});
					})
				);
			})
		).toPromise().then(result => {
			if (!result || !result.common) {
				throw new Error('Failed to load configuration');
			}

			try {
				const {common, institution} = result;
				const allLoanPurposes = {...(institution?.common?.loanPurpose || {})};
				// Filter loan purposes to include only those with "DEPOSIT"
				if (institution?.common?.loanPurpose) {
					institution.common.loanPurpose = Object.fromEntries(
						Object.entries(institution.common.loanPurpose).filter(([_, value]: [string, any]) =>
							value.productName.includes('DEPOSIT')
						)
					);
				}

				// Merge configurations: common -> institution common -> institution CXD
				const mergedCommon = this.utilsService.mergeDeep(common, institution?.common || {});

				AppConfigService.config = this.utilsService.mergeDeep(mergedCommon, institution?.CXD || {});

				AppConfigService.config.allLoanPurposes = allLoanPurposes;
				// Notify subscribers of the updated configuration
				this.onAppConfigUpdated$.next(AppConfigService.config);
			} catch (processingError) {
				console.error('Error processing config data:', processingError);
				throw processingError;
			}
		});
	}

	/**
	 * Loads configuration from local files.
	 * Falls back to a default configuration if institution-specific config fails.
	 * @returns A promise that resolves when the configuration is successfully loaded.
	 */
	private loadLocalConfig(): Promise<void> {

		return firstValueFrom(
			forkJoin({
				institutionConfig: this.instPath(),
				defaultConfig: this.defaultPath()
			})
		)
			// @ts-ignore
			.then(({institutionConfig, defaultConfig}) => {
				if (!institutionConfig && !defaultConfig) {
					throw new Error('Failed to load both institution-specific and default configurations');
				}

				// Use institution-specific config if available, otherwise fallback to default config
				const config = (institutionConfig as any)?.config || (defaultConfig as any)?.config;

				// Update the static config object with the loaded configuration
				AppConfigService.config = config;
				this.onAppConfigUpdated$.next(AppConfigService.config);
			}).catch((err: Error) => {
				console.error('Failed to load local config:', err);
				throw err;
			});
	}

	/**
	 * Retrieves a value from the configuration by key.
	 * @param key The key to retrieve the value for.
	 * @returns The value associated with the key.
	 */
	get(key: string): any {
		// Method implementation
	}

	/**
	 * Sets a value in the configuration for a given key.
	 * @param key The key to set the value for.
	 * @param value The value to set.
	 */
	set(key: string, value: any): void {
		// Method implementation
	}
}
